import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Table, Row, Col } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import CreatableSelect from 'react-select/creatable';
import Swal from "sweetalert2";
import { getIssueReasonsApi, createIssueApi, getAllProductsApi } from "../menu/service/api_service";
import { getAllInventoryApi } from "./service/api_service";

export default function NewIssueModal({ show, onHide, onSuccess }) {
  const token = localStorage.getItem("token");

  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);
  
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  
  const [issueList, setIssueList] = useState([{ type: 'item', target_id: '', quantity: '' }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchData();
      setIssueList([{ type: 'item', target_id: '', quantity: '' }]);
      setSelectedReason(null);
    }
  }, [show]);

  const fetchData = async () => {
    try {
      const [resItems, resProducts, resReasons] = await Promise.all([
        getAllInventoryApi(token),
        getAllProductsApi(token),
        getIssueReasonsApi(token)
      ]);
      setItems(resItems.data || []);
      setProducts(resProducts.data || []);
      
      const reasonOptions = (resReasons.data || []).map(r => ({ value: r, label: r }));
      setReasons(reasonOptions);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const handleAddRow = () => {
    setIssueList([...issueList, { type: 'item', target_id: '', quantity: '' }]);
  };

  const handleRemoveRow = (index) => {
    const list = [...issueList];
    list.splice(index, 1);
    setIssueList(list);
  };

  const handleChangeRow = (index, field, value) => {
    const list = [...issueList];
    list[index][field] = value;
    if (field === 'type') {
      list[index]['target_id'] = ''; // reset selection when type changes
    }
    setIssueList(list);
  };

  const handleSubmit = async () => {
    if (!selectedReason) {
      return Swal.fire("Error", "กรุณาระบุเหตุผลการเบิก", "error");
    }

    const invalidRow = issueList.find(row => !row.target_id || !row.quantity || Number(row.quantity) <= 0);
    if (invalidRow) {
      return Swal.fire("Error", "Please fill out all fields and ensure quantity > 0", "error");
    }

    if (issueList.length === 0) {
      return Swal.fire("Error", "You must add at least one item.", "error");
    }

    // Validate stock for inventory items
    const requestedQuantities = {};
    for (const row of issueList) {
      if (row.type === 'item' && row.target_id) {
        requestedQuantities[row.target_id] = (requestedQuantities[row.target_id] || 0) + Number(row.quantity);
      }
    }

    for (const itemId in requestedQuantities) {
      const item = items.find(i => i.id == itemId);
      if (item && requestedQuantities[itemId] > item.stock_quantity) {
        return Swal.fire("Error", `ไม่สามารถเบิกวัตถุดิบ "${item.name}" เกินจำนวนที่มีในสต๊อกได้ (สต๊อกปัจจุบัน: ${item.stock_quantity} ${item.unit})`, "error");
      }
    }

    try {
      setLoading(true);
      await createIssueApi(token, {
        reason: selectedReason.value,
        items: issueList.map(row => ({
          type: row.type,
          target_id: Number(row.target_id),
          quantity: Number(row.quantity)
        }))
      });

      Swal.fire("Success", "Issue Document created successfully", "success");
      onSuccess();
      onHide();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to create issue document", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold" style={{ color: "#1e3c72" }}>New Issue Document</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-4">
          <Form.Label className="fw-bold">Reason</Form.Label>
          <CreatableSelect
            isClearable
            options={reasons}
            value={selectedReason}
            onChange={(val) => setSelectedReason(val)}
            placeholder="Select or type to create a new reason..."
            formatCreateLabel={(inputValue) => `Create reason "${inputValue}"`}
          />
        </Form.Group>

        <h6 className="fw-bold mb-3 border-bottom pb-2">Items to Issue</h6>
        
        <Table bordered hover size="sm" className="align-middle">
          <thead className="bg-light">
            <tr>
              <th width="15%">Type</th>
              <th width="40%">Product / Item</th>
              <th width="15%">Quantity</th>
              <th width="15%">Unit</th>
              <th width="15%" className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {issueList.map((row, index) => (
              <tr key={index}>
                <td>
                  <Form.Select 
                    size="sm"
                    value={row.type} 
                    onChange={(e) => handleChangeRow(index, 'type', e.target.value)}
                  >
                    <option value="item">Raw Material</option>
                    <option value="product">Product</option>
                  </Form.Select>
                </td>
                <td>
                  <Form.Select 
                    size="sm"
                    value={row.target_id} 
                    onChange={(e) => handleChangeRow(index, 'target_id', e.target.value)}
                  >
                    <option value="">-- Select Item --</option>
                    {row.type === 'item' ? (
                      items.map(i => <option key={i.id} value={i.id}>{i.name} (Stock: {i.stock_quantity} {i.unit})</option>)
                    ) : (
                      products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)
                    )}
                  </Form.Select>
                </td>
                <td>
                  <Form.Control 
                    size="sm"
                    type="number" 
                    min="1" 
                    max={row.type === 'item' && row.target_id ? items.find(i => i.id == row.target_id)?.stock_quantity : undefined}
                    step="0.01"
                    placeholder="Qty"
                    value={row.quantity}
                    onChange={(e) => handleChangeRow(index, 'quantity', e.target.value)}
                  />
                </td>
                <td className="align-middle text-center text-muted">
                  {row.type === 'item' && row.target_id 
                    ? items.find(i => i.id == row.target_id)?.unit || '-' 
                    : (row.type === 'product' && row.target_id ? 'pcs' : '-')}
                </td>
                <td className="text-center">
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={() => handleRemoveRow(index)}
                    disabled={issueList.length === 1}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Button variant="outline-primary" size="sm" onClick={handleAddRow}>
          <FaPlus className="me-1"/> Add Row
        </Button>
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={loading}
          style={{ background: "#1e3c72", border: "none" }}
        >
          {loading ? "Saving..." : "Confirm Issue"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
